import Axios from 'functions/Axios';
import { GET_QR_CODE_SVG_URL } from 'components/Dam/DAM_API/urls';

/**
 * The svg endpoint regenerates the same markup for a given code, so the response is
 * cached per (qrCodeId + updatedAt). An edit changes updatedAt, which busts the entry
 * on its own without any manual invalidation.
 */
const svgCache = new Map();

export const qrCacheKey = (item) => `${item?.qrCodeId ?? ''}:${item?.updatedAt ?? ''}`;

export const fetchQrSvg = (item) => {
  const key = qrCacheKey(item);
  if (svgCache.has(key)) return svgCache.get(key);

  // The promise itself is cached so two cards mounted at once share a single request.
  const request = Axios.get(GET_QR_CODE_SVG_URL(item?.qrCodeId), { responseType: 'text' })
    .then(({ data }) => (typeof data === 'string' ? data : ''))
    .catch((err) => {
      svgCache.delete(key);
      throw err;
    });

  svgCache.set(key, request);
  return request;
};

export const clearQrSvgCache = () => svgCache.clear();

export const sanitizeFileName = (name = 'qr-code') =>
  (String(name).trim() || 'qr-code').replace(/[^a-z0-9-_ ]/gi, '_').slice(0, 60);

const triggerDownload = (href, fileName) => {
  const link = document.createElement('a');
  link.href = href;
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/** The file name the server suggests for a download, when it sends one. */
export const parseContentDispositionName = (contentDisposition) => {
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(contentDisposition ?? '');
  if (!match) return '';
  try {
    return decodeURIComponent(match[1]).trim();
  } catch (err) {
    return match[1].trim();
  }
};

/** Saves a Blob (a generated PDF or workbook) under the given file name. */
export const downloadBlob = (blob, fileName) => {
  const url = window.URL.createObjectURL(blob);
  triggerDownload(url, fileName);
  // revoking in the same tick can cancel the download in some browsers
  setTimeout(() => window.URL.revokeObjectURL(url), 1000);
};

// Only these two exist as files; JPEG used to come from redrawing the svg on a canvas
export const QR_DOWNLOAD_FORMATS = [
  { key: 'png', label: 'Download PNG', short: 'PNG' },
  { key: 'svg', label: 'Download SVG', short: 'SVG' },
];

/**
 * The list sends one PNG URL per code and the SVG sits beside it under the same name, so
 * switching format is an extension swap rather than another API call.
 */
export const qrAssetUrl = (item, format = 'png') => {
  const preview = item?.qrPreview;
  if (!preview) return '';
  return String(preview).replace(/\.(png|svg)(?=$|[?#])/i, `.${format}`);
};

const blobToDataUrl = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error ?? new Error('Could not read the image'));
    reader.readAsDataURL(blob);
  });

/** The stored image as a data URL, for embedding in the analytics report. */
export const fetchQrAssetDataUrl = async (item, format = 'png') => {
  const url = qrAssetUrl(item, format);
  if (!url) return null;

  // no credentials: the asset is a plain file, and an auth header would force a preflight
  const response = await fetch(url, { credentials: 'omit' });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return blobToDataUrl(await response.blob());
};

/** Rasterises QR svg markup onto an opaque white square and returns a PNG data URL. */
export const svgToDataUrl = (svgMarkup, { size = 1024 } = {}) =>
  new Promise((resolve, reject) => {
    if (!svgMarkup) {
      reject(new Error('No QR markup to convert'));
      return;
    }

    const url = window.URL.createObjectURL(
      new Blob([svgMarkup], { type: 'image/svg+xml;charset=utf-8' }),
    );
    const image = new Image();

    image.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d');
        // QR readers need the quiet zone opaque: a transparent PNG scans badly on dark pages
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, size, size);
        ctx.drawImage(image, 0, 0, size, size);
        resolve(canvas.toDataURL('image/png'));
      } catch (err) {
        reject(err);
      } finally {
        window.URL.revokeObjectURL(url);
      }
    };

    image.onerror = (err) => {
      window.URL.revokeObjectURL(url);
      reject(err);
    };

    image.src = url;
  });

/**
 * Saves the stored image for a code. The file lives on the DAM host, so a cross-origin
 * `<a download>` would be ignored and the bytes are fetched instead. Returns 'opened' when
 * that host refuses the read and the file had to be opened in a tab for manual saving.
 */
export const downloadQrCode = async (item, format = 'png') => {
  const url = qrAssetUrl(item, format);
  if (!url) throw new Error('This code has no image yet');

  const fileName = `${sanitizeFileName(item?.title || item?.qrCodeId)}.${format}`;
  try {
    const response = await fetch(url, { credentials: 'omit' });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    downloadBlob(await response.blob(), fileName);
    return 'saved';
  } catch (err) {
    console.error('QR download fell back to opening the file', err);
    window.open(url, '_blank', 'noopener,noreferrer');
    return 'opened';
  }
};

/**
 * The create response is the only place a brand new qrCodeId comes from, and the
 * payload shape differs between wrappers, so every known form is accepted before
 * giving up. Returns null when the id cannot be found, and the caller just closes.
 */
export const resolveCreatedQrCode = (response, requestBody = {}) => {
  const payload = response?.data ?? response;

  if (typeof payload === 'string' && payload.trim()) {
    return { ...requestBody, qrCodeId: payload.trim() };
  }
  if (payload?.qrCodeId) {
    return { ...requestBody, ...payload };
  }
  if (response?.qrCodeId) {
    return { ...requestBody, ...response };
  }
  return null;
};

/**
 * A code either points everyone at one URL (STANDARD), or carries an Android and an
 * iOS URL and lets the backend redirect per the scanning device (DEVICE).
 */
export const QR_TYPES = { STANDARD: 1, DEVICE: 2 };

export const QR_TYPE_OPTIONS = [
  { key: QR_TYPES.STANDARD, label: 'Single URL' },
  { key: QR_TYPES.DEVICE, label: 'Android / iOS' },
];

/**
 * Carrying either device URL counts, not just an explicit qrType: the list response
 * does not reliably echo qrType back, and gating on it alone left device codes showing
 * no URL at all.
 */
export const isDeviceQr = (item) =>
  Number(item?.qrType) === QR_TYPES.DEVICE || Boolean(item?.androidUrl || item?.iosUrl);

/** The URLs a code actually points at, labelled, skipping the ones left blank. */
export const getQrTargets = (item) => {
  const deviceTargets = [
    { label: 'Android', url: item?.androidUrl },
    { label: 'iOS', url: item?.iosUrl },
  ].filter((target) => Boolean(target.url));

  if (deviceTargets.length > 0) return deviceTargets;

  return item?.destinationUrl ? [{ label: '', url: item.destinationUrl }] : [];
};

export const isExternalLink = (value = '') => /^https?:\/\//i.test(String(value).trim());

export const withProtocol = (value = '') => {
  const trimmed = String(value).trim();
  return isExternalLink(trimmed) ? trimmed : `https://${trimmed}`;
};
